// src/components/AgencyAuditTools.js
// Outils agence sur le détail d'un audit (plan Enterprise) : client rattaché, PDF à sa marque, lien de partage.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, Download, Link2, Copy, Loader2, XCircle, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { downloadAuditPDF } from '../utils/pdfGenerator';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';
const btn = 'inline-flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-50';
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

export default function AgencyAuditTools({ audit, onAuditChange }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [clients, setClients] = useState([]);
  const [share, setShare] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API_URL}/api/clients`, { headers: h }).then((r) => setClients(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/api/audits/${audit.audit_id}/share`, { headers: h }).then((r) => setShare(r.data.share)).catch(() => {});
  }, [audit.audit_id, token]);

  const client = clients.find((c) => c.client_id === audit.client_id);

  const changeClient = async (clientId) => {
    try {
      await axios.patch(`${API_URL}/api/audits/${audit.audit_id}/client`, { client_id: clientId || null }, { headers });
      onAuditChange({ ...audit, client_id: clientId || null });
      toast.success(clientId ? 'Audit rattaché au client' : 'Audit détaché du client');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const pdf = async () => {
    setBusy('pdf');
    try {
      const [b, reports] = await Promise.all([
        axios.get(`${API_URL}/api/agency/branding`, { headers }),
        axios.get(`${API_URL}/api/reports`, { headers }).catch(() => ({ data: [] })),
      ]);
      const report = (reports.data || []).find((r) => r.audit_id === audit.audit_id) || null;
      await downloadAuditPDF(audit, { branding: b.data, report, clientName: client?.name });
    } catch (err) {
      console.error(err);
      toast.error('Le PDF n’a pas pu être généré');
    } finally {
      setBusy(null);
    }
  };

  const createShare = async () => {
    setBusy('share');
    try {
      const r = await axios.post(`${API_URL}/api/audits/${audit.audit_id}/share`, { expires_days: days }, { headers });
      setShare(r.data.share);
      setShareUrl(r.data.url);
      try { await navigator.clipboard.writeText(r.data.url); toast.success('Lien créé et copié'); }
      catch { toast.success('Lien créé'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(null);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Désactiver ce lien ? Votre client ne pourra plus ouvrir le rapport.')) return;
    await axios.delete(`${API_URL}/api/audits/${audit.audit_id}/share`, { headers });
    setShare(null);
    setShareUrl('');
    toast.success('Lien désactivé');
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Lien copié'); }
    catch { toast.error('Copie impossible, sélectionnez le lien à la main'); }
  };

  return (
    <div className="bg-zinc-900 border border-purple-500/20 rounded-sm" data-testid="agency-tools">
      <div className="px-6 py-4 border-b border-zinc-800 bg-black/20 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-white font-mono font-bold text-xs uppercase tracking-widest flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-purple-400" /> Espace agence
        </h2>
        <Link to="/agence" className="text-zinc-500 hover:text-white font-mono text-[10px] uppercase">Ma marque →</Link>
      </div>
      <div className="p-6 grid gap-6 md:grid-cols-3">
        {/* Client */}
        <div className="space-y-2">
          <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">Client</p>
          <select value={audit.client_id || ''} onChange={(e) => changeClient(e.target.value)}
            className="w-full bg-black border border-zinc-800 p-2.5 text-white font-mono text-xs focus:border-purple-500 outline-none" aria-label="Client rattaché">
            <option value="">— Aucun client —</option>
            {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
          </select>
          {clients.length === 0 && <Link to="/clients" className="text-purple-300 hover:underline text-xs">Créer une fiche client</Link>}
        </div>

        {/* PDF */}
        <div className="space-y-2">
          <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">Rapport PDF</p>
          <button onClick={pdf} disabled={busy === 'pdf'} className={`${btn} w-full bg-purple-600 hover:bg-purple-700 text-white`}>
            {busy === 'pdf' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Télécharger le PDF
          </button>
          <p className="text-zinc-600 text-xs">À votre logo et vos couleurs, avec le rapport IA s'il a été généré.</p>
        </div>

        {/* Partage */}
        <div className="space-y-2">
          <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">Lien pour le client</p>
          {shareUrl ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input readOnly value={shareUrl} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 bg-black border border-zinc-800 px-2 py-2 text-xs font-mono text-zinc-200" aria-label="Lien de partage" />
                <button onClick={copy} className="p-2 border border-zinc-700 hover:border-purple-500 text-zinc-300" aria-label="Copier le lien"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-amber-300/80 text-xs">Copiez-le maintenant : pour des raisons de sécurité, il ne sera plus affiché.</p>
            </div>
          ) : share ? (
            <p className="text-zinc-300 text-xs flex items-center gap-2"><Link2 className="w-3 h-3 text-green-400" /> Lien actif jusqu'au {fmt(share.expires_at)}</p>
          ) : (
            <p className="text-zinc-500 text-xs">Une page à vos couleurs, consultable sans compte.</p>
          )}
          {share && (
            <p className="text-zinc-600 text-xs flex items-center gap-1"><Eye className="w-3 h-3" /> {share.views || 0} consultation{share.views > 1 ? 's' : ''}</p>
          )}
          <div className="flex gap-2 items-center">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="bg-black border border-zinc-800 p-2 text-white font-mono text-xs" aria-label="Durée du lien">
              <option value={7}>7 jours</option>
              <option value={30}>30 jours</option>
              <option value={90}>90 jours</option>
              <option value={365}>1 an</option>
            </select>
            <button onClick={createShare} disabled={busy === 'share'} className={`${btn} flex-1 border border-purple-500/40 text-purple-200 hover:bg-purple-500/10`}>
              {busy === 'share' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />} {share ? 'Nouveau lien' : 'Créer le lien'}
            </button>
          </div>
          {share && (
            <button onClick={revoke} className="text-red-400 hover:text-red-300 text-xs inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Désactiver le lien</button>
          )}
        </div>
      </div>
    </div>
  );
}
